import React, { useState, useEffect, useRef } from 'react';
import type { SymbolSearchResult } from '../../types';
import { apiService } from '../../services/api';

interface SymbolSearchProps {
  value: string;
  onChange: (symbol: string) => void;
  exchange?: 'NSE' | 'BSE';
  placeholder?: string;
  className?: string;
  onSymbolSelect?: (symbol: SymbolSearchResult) => void;
  disabled?: boolean;
  error?: string;
}

const SymbolSearch: React.FC<SymbolSearchProps> = ({
  value,
  onChange,
  exchange,
  placeholder = 'Search symbols...',
  className = '',
  onSymbolSelect,
  disabled = false,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SymbolSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value);
  const [isSelecting, setIsSelecting] = useState(false); // Track if we're in selection mode
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if the current value exactly matches one of the search results
  const isCompleteSymbol = (symbol: string) => {
    const trimmed = symbol.trim();
    return searchResults.some(result => result.symbol === trimmed);
  };

  // Debounce search
  useEffect(() => {
    // Don't search if we're in selection mode
    if (isSelecting) {
      setIsSelecting(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch();
      } else {
        setSearchResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, exchange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async () => {
    if (searchQuery.length < 2) return;

    setLoading(true);
    try {
      const params: any = {
        query: searchQuery,
        limit: 5
      };

      if (exchange) {
        params.exchange = exchange;
      }

      const response = await apiService.symbols.search(params);
      setSearchResults(response.data || []);
      // Only show suggestions if current value doesn't exactly match any result
      setIsOpen(!!(response.data && response.data.length > 0 && !isCompleteSymbol(searchQuery)));
    } catch (error: any) {
      console.error('Error searching symbols:', error);
      setSearchResults([]);
      setIsOpen(false);
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        console.error('Authentication failed - user may need to login again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
    // Show suggestions if long enough and doesn't match any result
    setIsOpen(newValue.length >= 2 && !isCompleteSymbol(newValue));
  };

  const handleSymbolSelect = (symbol: SymbolSearchResult) => {
    // Set selection mode to prevent search trigger
    setIsSelecting(true);
    
    setSearchQuery(symbol.symbol);
    onChange(symbol.symbol);
    setIsOpen(false);
    setSearchResults([]);
    
    if (onSymbolSelect) {
      onSymbolSelect(symbol);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleSymbolSelect(searchResults[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Only show suggestions on focus if not a complete symbol
            if (searchQuery.length >= 2 && !isCompleteSymbol(searchQuery)) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          `}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}

      {isOpen && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {searchResults.map((result) => (
            <div
              key={`${result.symbol}-${result.exchange}`}
              onClick={() => handleSymbolSelect(result)}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-gray-900">{result.symbol}</div>
                  {result.company_name && (
                    <div className="text-sm text-gray-600 truncate">
                      {result.company_name}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {result.exchange}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen && searchQuery.length >= 2 && !loading && searchResults.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-3 py-2 text-gray-500 text-sm">
            No symbols found for "{searchQuery}"
          </div>
        </div>
      )}
    </div>
  );
};

export default SymbolSearch; 